import { ChangeDetectorRef, Component, QueryList, ViewChild, ViewChildren, ViewEncapsulation, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { isNumber, isNullOrUndefined } from '../../../models/utils';
import { ScrollablePopoverDirective } from '../../../directives/scrollable-popover/scrollable-popover.directive';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DataArea } from '../../../models/data-area';
import { DrObject } from '../../../models/dr-object';
import { Endpoint, HelpIndex, MessageType, UpdateStatusType } from '../../../models/enums';
import { ExportMetadataProfile } from '../../../models/export-metadata-profile';
import { ExportStream } from '../../../models/export-stream';
import { ExportDisabled, ExportExtraOptions, ExportOnChange, ExportStreamOptions, ExportUpdateDate, ExportValidation, ExportStreamOptionsViewModel } from '../../../models/export-stream-options';
import { ExportVolume } from '../../../models/export-volume';
import { Template, TemplateType } from '../../../models/template';
import { Timezone } from '../../../models/timezone';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { ExportOutputFormattingOptionsComponent } from '../../export/export-output-formatting-options/export-output-formatting-options.component';
import { TimezoneModalComponent } from '../timezone-preferences/timezone-modal.component';
import { ExportComponent } from '../../export/export.component';
import { XMLService } from '../../../services/xml/xml.service';
import { ActionType, ObjectActionListType, Permissions } from '../../../models/permission';
import { MAXIMUM_EMBEDDED_TEXT_SIZE } from '../../../models/constants';

@Component({
    standalone: false,
    selector: 'res-generate-load-file-modal',
    templateUrl: './generate-load-file-modal.component.html',
    styleUrls: ['./generate-load-file-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class GenerateLoadFileModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(ExportOutputFormattingOptionsComponent, { static: true }) exportOutputFormattingOptionsComponent: ExportOutputFormattingOptionsComponent;
    @ViewChildren(ScrollablePopoverDirective) errorPopovers: QueryList<ScrollablePopoverDirective>;

    automationId = 'export-modal';
    modalClass = 'generate-load-file-modal';
    modalHelpIndex = HelpIndex.exports_generate_load_file_dlg;
    isBusy = false;
    isBusyMessage: string;

    exportStream: ExportStream;
    exportVolume: ExportVolume;
    isExportVolume: boolean;
    maxTableNameLength = ExportStreamOptionsViewModel.EXPORT_OPTION_TABLE_NAME_MAX_LENGTH;

    exportOptions: ExportStreamOptions;
    exportComponent: ExportComponent;

    isSystemUserWithPermissions = false;

    extraOptions: Partial<ExportExtraOptions> = {
        isBusy: new Array<Endpoint>(),
        exportDataAreas: new Array<DataArea>(),
        exportFieldTemplates: {
            project: new Array<ExportMetadataProfile | Template>(),
            organization: new Array<ExportMetadataProfile | Template>(),
            system: new Array<ExportMetadataProfile | Template>()
        },
        includeFullText: false,
        selectedExportDatabaseConnection: null,
        maxRecordsPerFile: false,
        consolidatedManifest: false,
        timezones: new Array<Timezone>(),
        allTimezones: new Array<Timezone>(),
        prevSelectedTimezone: null,
        dateFormats: ['MM/dd/yyyy', 'MM/dd/yy', 'yy/MM/dd', 'yyyy/MM/dd', 'dd/MMM/yy'],
        delimiters: [';', '-', 'Space'],
        timeFormats: ['HH:mm:ss', 'hh:mm:ss a', 'h:mm:ss a'],
        updateDates: new Array<ExportUpdateDate>()
    };

    onChange: Partial<ExportOnChange> = {
        selectedTimezone: () => {
            if (this.exportOptions.timezone === 'Other') {
                this.commonService.openModal(TimezoneModalComponent, { modalData: this.extraOptions.allTimezones }).then((value) => {
                    if (value.modalResult && value.modalData instanceof Timezone) {
                        this.extraOptions.timezones.splice(-1, 1);
                        this.extraOptions.timezones.push(value.modalData);

                        this.extraOptions.timezones.push(new Timezone({
                            description: 'Other...',
                            id: 'Other'
                        }));

                        this.exportOptions.timezone = value.modalData.id;
                        this.extraOptions.prevSelectedTimezone = this.exportOptions.timezone;
                    } else {
                        this.exportOptions.timezone = this.extraOptions.prevSelectedTimezone;
                    }
                });
            } else {
                this.extraOptions.prevSelectedTimezone = this.exportOptions.timezone;
            }
        },
        tableName: {
            keyboard: (): void => {
                // this.tableNameNeedsValiation = true;
                this.tableNameCheck();
            },
            paste: (text: string): void => {
                // this.tableNameNeedsValiation = true;
                if (!isNullOrUndefined(text)) {
                    text.trim();
                    this.exportOptions.tableName = this.maxTableNameLength ? text.substr(0, this.maxTableNameLength) : text;
                    this.tableNameCheck();
                }
            },
        },
        outputOptions: (optionName: string) => {
            switch (optionName) {
                case 'DAT': {
                    // exportOptions.generateConcordance
                    this.exportOptions.concordanceUnicodeFormat = this.exportOptions.generateConcordance ? false : null;
                    if (!this.exportOptions.generateConcordance && !this.exportOptions.generateCsv && !this.exportOptions.generateEdrmXml) {
                        this.extraOptions.maxRecordsPerFile = false;
                        this.validation.maxManifestRecords.invalid = false;
                        this.exportOptions.maxManifestRecords = 0;
                    }
                    if (!this.exportOptions.generateConcordance && !this.exportOptions.generateEdrmXml && !this.exportOptions.exportToDb) {
                        this.extraOptions.includeFullText = false;
                        this.exportOptions.maximumEmbeddedTextSize = null;
                    }
                    break;
                }
                case 'CSV': {
                    // exportOptions.generateCsv
                    if (!this.exportOptions.generateConcordance && !this.exportOptions.generateCsv && !this.exportOptions.generateEdrmXml) {
                        this.extraOptions.maxRecordsPerFile = false;
                        this.validation.maxManifestRecords.invalid = false;
                        this.exportOptions.maxManifestRecords = 0;
                    }
                    break;
                }
                case 'EDRM XML': {
                    // exportOptions.generateEdrmXml
                    if (!this.exportOptions.generateConcordance && !this.exportOptions.generateCsv && !this.exportOptions.generateEdrmXml) {
                        this.extraOptions.maxRecordsPerFile = false;
                        this.validation.maxManifestRecords.invalid = false;
                        this.exportOptions.maxManifestRecords = 0;
                    }
                    if (!this.exportOptions.generateConcordance && !this.exportOptions.generateEdrmXml && !this.exportOptions.exportToDb) {
                        this.extraOptions.includeFullText = false;
                        this.exportOptions.maximumEmbeddedTextSize = null;
                    }
                    break;
                }
                case 'SQL DB': {
                    // exportOptions.exportToDb
                    if (!this.exportOptions.generateConcordance && !this.exportOptions.generateEdrmXml && !this.exportOptions.exportToDb) {
                        this.extraOptions.includeFullText = false;
                        this.exportOptions.maximumEmbeddedTextSize = null;
                    }
                    this.validation.tableName.invalid = false;
                    this.exportOptions.tableName = null;
                    this.extraOptions.selectedExportDatabaseConnection = null;
                    this.exportComponent.tableNameDescriptionComponent.name = '';
                    if (this.exportOptions.exportToDb) {
                        this.getExportDatabaseConnection();
                    }
                    break;
                }
            }
            if (this.disabled.noOfOutputFormats() === 6) {
                this.exportOptions.postProcess = false;
                this.exportOptions.postProcessDataAreaHandle = null;
                this.exportOptions.includeVolumeLabelInPath = false;
            }
        },
        maxRecordsPerFile: () => {
            if (!this.extraOptions.maxRecordsPerFile) {
                this.validation.maxManifestRecords.invalid = false;
            }
        },
        consolidatedManifest: () => {
            this.exportOptions.includeVolumeLabelInPath = this.extraOptions.consolidatedManifest;
        },
        duplicateOverlays: () => {
            if (!this.exportOptions.overlayManifest) {
                this.exportOptions.includeAllMasterDuplicates = false;
                this.exportOptions.overlayMetadataTemplateHandle = null;
            } else {
                if (!this.exportOptions.overlayMetadataTemplateHandle) {
                    this.exportOptions.overlayMetadataTemplateHandle = this.extraOptions.exportFieldTemplates.project[0].handle;
                }
            }
        },
        includeFullText: () => {
            if (!this.extraOptions.includeFullText) {
                this.exportOptions.excludeEmailHeadersInLoadFile = false;
            }
        }
    };

    disabled: Partial<ExportDisabled> = {
        includeThreadAttach: (): boolean => {
            return !this.exportOptions.includeSeperateEmailAttachments && !this.exportOptions.includeOLEChildren;
        },
        includeFullText: (): boolean => {
            return (!this.exportOptions.generateConcordance && !this.exportOptions.generateEdrmXml && !this.exportOptions.exportToDb) || !this.exportOptions.includeTextConversion;
        },
        maxRecordsPerFile: (): boolean => {
            return !this.exportOptions.generateConcordance && !this.exportOptions.generateCsv && !this.exportOptions.generateEdrmXml;
        },
        exportToDb: (): boolean => {
            return true;
            // TODO: put back when ST is done http://172.31.210.172:8080/browse/ECASL-30725
            // !this.exportOptions.exportToDb || !this.extraOptions.selectedExportDatabaseConnection;
        },
        noOfOutputFormats: (): number => {
            let count = 0;
            if (!this.exportOptions.generateConcordance) {
                count++;
            }
            if (!this.exportOptions.generateLst) {
                count++;
            }
            if (!this.exportOptions.generateDii) {
                count++;
            }
            if (!this.exportOptions.generateCsv) {
                count++;
            }
            if (!this.exportOptions.generateEdrmXml) {
                count++;
            }
            if (!this.exportOptions.exportToDb) {
                count++;
            }
            return count;
        }
    };

    validation: Partial<ExportValidation> = {
        drObject: new DrObject(),
        dataAreaHandle: {
            check: (): void => {
                this.validation.dataAreaHandle.invalid = !(this.exportOptions.dataAreaHandle);
            },
            invalid: false,
            hasError: false,
            errorMessage: `Please select an Export Data Area.`
        },
        tableName: {
            validateName: (): void => {
                if (this.exportComponent.tableNameNeedsValidation) {
                    this.validateTableName();
                    this.exportOptions.tableName = this.exportComponent.tableNameDescriptionComponent.name;
                    this.exportComponent.tableNameNeedsValidation = false;
                }
            },
            checkForErrors: (): void => {
                this.tableNameCheckForErrors();
            },
            isDisabled: (): boolean => {
                return true;
                // TODO: put back once ST is done
                // return this.isTableNameDisabled();
            },
            isCheckboxDisabled: (): boolean => {
                return true;
                // TODO: put back once ST is done
                // return this.isSQLCheckboxDisabled();
            },
            lengthValidation: (): boolean => {
                return true;
            },
            invalid: false,
            errorMessage: '',
            errorType: 'error',
            hasError: false,
            hasValidCharacters: true
        },
        maxManifestRecords: {
            check: (): void => {
                this.validation.maxManifestRecords.invalid = this.extraOptions.maxRecordsPerFile && !isNumber(this.exportOptions.maxManifestRecords);
            },
            invalid: false,
            hasError: false,
            errorMessage: `Please specify Max Records Per File.`
        },
        dateFormat: {
            check: (): void => {
                this.validation.dateFormat.invalid = this.validation.dateFormat.hasError = !(this.exportOptions.dateFormat) && !isNullOrUndefined(this.exportOptions.dateFormat);
            },
            checkForErrors: (): void => {
                if (this.validation.dateFormat.invalid) {
                    this.validation.dateFormat.check();
                }
            },
            invalid: false,
            errorMessage: `Please specify a Date Format.`,
            hasError: false
        },
        dateTimeDelimiter: {
            check: (): void => {
                this.validation.dateTimeDelimiter.invalid = this.validation.dateTimeDelimiter.hasError = !(this.exportOptions.dateTimeDelimiter) && !isNullOrUndefined(this.exportOptions.dateTimeDelimiter);
            },
            checkForErrors: (): void => {
                if (this.validation.dateTimeDelimiter.invalid) {
                    this.validation.dateTimeDelimiter.check();
                }
            },
            invalid: false,
            errorMessage: `Please specify a Delimiter.`,
            hasError: false
        },
        timeFormat: {
            check: (): void => {
                this.validation.timeFormat.invalid = this.validation.timeFormat.hasError = !(this.exportOptions.timeFormat) && !isNullOrUndefined(this.exportOptions.timeFormat);
            },
            checkForErrors: (): void => {
                if (this.validation.timeFormat.invalid) {
                    this.validation.timeFormat.check();
                }
            },
            invalid: false,
            errorMessage: `Please specify a Time Format.`,
            hasError: false
        },
        valid: (): boolean => {
            let invalid = false;
            invalid = invalid || this.validation.tableName.invalid;
            this.validation.dataAreaHandle.check();
            invalid = invalid || this.validation.dataAreaHandle.invalid;
            this.validation.dateFormat.check();
            invalid = invalid || this.validation.dateFormat.invalid;
            this.validation.dateTimeDelimiter.check();
            invalid = invalid || this.validation.dateTimeDelimiter.invalid;
            this.validation.timeFormat.check();
            invalid = invalid || this.validation.timeFormat.invalid;
            invalid = invalid || this.validation.tableName.invalid;
            this.validation.maxManifestRecords.check();
            invalid = invalid || this.validation.maxManifestRecords.invalid;
            this.changeDetector.detectChanges();
            if (invalid) {
                if (this.errorPopovers) {
                    let visiblePopovers = this.errorPopovers.toArray().concat(this.exportOutputFormattingOptionsComponent.errorPopovers.toArray()).filter(popover => popover.resScrollablePopover);
                    if (visiblePopovers && visiblePopovers.length > 0) {
                        visiblePopovers = visiblePopovers.sort((a, b) => {
                            const rectA = a.inputElement.getBoundingClientRect();
                            const rectB = b.inputElement.getBoundingClientRect();
                            if (rectA.top > rectB.top) { return 1; }
                            if (rectA.top < rectB.top) { return -1; }
                            if (rectA.top === rectB.top) { return 0; }
                        });
                        visiblePopovers[0].focusOnInput();
                    }
                }
            }
            return !invalid;
        }
    };

    constructor(
        private modalService: BsModalService,
        public deleteEntityRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService,
        private xmlService: XMLService,
        private changeDetector: ChangeDetectorRef) {
        super(modalService, deleteEntityRef);
        this.exportOptions = new ExportStreamOptions();
        // instantiated so that we may use some shared methods.
        this.exportComponent = new ExportComponent(commonService, restService, xmlService, changeDetector);

        this.isExportVolume = false;
        if (this.modalData && this.modalData.exportStream instanceof ExportStream) {
            this.exportStream = this.modalData.exportStream as ExportStream;
            if (this.modalData.exportVolume instanceof ExportVolume) {
                this.exportVolume = this.modalData.exportVolume as ExportVolume;
                this.isExportVolume = true;
            }
        }
    }

    ngOnInit(): void {
        this.isSystemUserWithPermissions = (this.commonService.currentUser?.importedFromSystemUser || this.commonService.currentUser?.originalCustomerName === 'super_system_customer') && Permissions.checkUserPermission(ObjectActionListType.SYSTEM_EXPORT_FIELDS, ActionType.VIEW);
        this.exportComponent.initializeTableNameDescriptionComponent();
        // TODO: remove this one line once ST is fixed.
        this.exportComponent.tableNameDescriptionComponent.name = '';
        // TODO: put back once ST is fixed
        // this.exportComponent.tableNameDescriptionComponent.name = this.exportOptions.tableName;
        this.exportComponent.setNameDescriptionComponentName(this.exportComponent.tableNameDescriptionComponent);
        this.loadExportDataAreas();
        this.loadExportFieldTemplates();
        this.loadUpdateDates();
        if (this.exportStream) {
            this.loadExportStreamOptions();
        }

        this.setDBTableStates();
    }

    identify(index: number, item: any) {
        return item.name;
    }

    loadExportDataAreas(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading...';
        this.extraOptions.isBusy.push(Endpoint.orgManager_listExportDataAreas);
        this.restService.post(Endpoint.orgManager_listExportDataAreas, {}).then(response => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.orgManager_listExportDataAreas), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
            if (response && response.exportDataAreas) {
                (response.exportDataAreas as Array<any>).map(responseItem => new DataArea(responseItem)).forEach((item) => {
                    this.extraOptions.exportDataAreas.push(item);
                });
            }
        }, error => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.orgManager_listExportDataAreas), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
        });
    }

    loadExportFieldTemplates(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading...';

        this.extraOptions.isBusy.push(Endpoint.projectManager_listExportMetadataProfile);
        this.restService.post(Endpoint.projectManager_listExportMetadataProfile, { handle: this.commonService.currentProject.handle }).then(response => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.projectManager_listExportMetadataProfile), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
            if (response && response.profiles) {
                (response.profiles as Array<any>).map(responseItem => new ExportMetadataProfile(responseItem)).forEach((item) => {
                    this.extraOptions.exportFieldTemplates.project.push(item);
                    if (!this.exportOptions.metadataTemplateHandle && item.defaultProfile) {
                        this.exportOptions.metadataTemplateHandle = item.handle;
                    }
                    if (!this.exportOptions.overlayMetadataTemplateHandle && item.defaultProfile && this.exportOptions.overlayManifest) {
                        this.exportOptions.overlayMetadataTemplateHandle = item.handle;
                    }
                });
            }
        }, error => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.projectManager_listExportMetadataProfile), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
        });

        this.extraOptions.isBusy.push(Endpoint.orgManager_listTemplates);
        this.restService.post(Endpoint.orgManager_listTemplates, { scope: 'ORG_LEVEL', tempType: TemplateType.EXPORT_FIELDS }).then(response => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.orgManager_listTemplates), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
            if (response && response.templates) {
                (response.templates as Array<any>).map(responseItem => new Template(responseItem)).forEach((item) => {
                    this.extraOptions.exportFieldTemplates.organization.push(item);
                });
            }
        }, error => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.orgManager_listTemplates), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
        });

        if (this.isSystemUserWithPermissions) {
            this.extraOptions.isBusy.push(Endpoint.orgManager_listTemplates);
            this.restService.post(Endpoint.orgManager_listTemplates, { scope: 'SYSTEM_LEVEL', tempType: TemplateType.EXPORT_FIELDS, systemScope: true }).then(response => {
                this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.orgManager_listTemplates), 1);
                this.isBusy = this.extraOptions.isBusy.length > 0;
                if (response && response.templates) {
                    (response.templates as Array<any>).map(responseItem => new Template(responseItem)).forEach((item) => {
                        this.extraOptions.exportFieldTemplates.system.push(item);
                    });
                }
            }, error => {
                this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.orgManager_listTemplates), 1);
                this.isBusy = this.extraOptions.isBusy.length > 0;
            });
        }
    }

    loadUpdateDates(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading...';
        this.extraOptions.isBusy.push(Endpoint.viewManager_listExportViewUpdateDates);
        this.restService.post(Endpoint.viewManager_listExportViewUpdateDates, { handle: this.exportVolume ? this.exportVolume.handle : this.exportStream.handle }).then(response => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.viewManager_listExportViewUpdateDates), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
            if (response && response.updateDates) {
                (response.updateDates as Array<string>).map(responseItem => new ExportUpdateDate({ name: responseItem, selected: false })).forEach((item) => {
                    this.extraOptions.updateDates.push(item);
                });
            }
        }, error => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.viewManager_listExportViewUpdateDates), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
        });
    }

    loadExportStreamOptions = () => {
        this.isBusy = true;
        this.isBusyMessage = 'Loading...';
        this.extraOptions.isBusy.push(Endpoint.exportStreamManager_getExportStream);
        this.restService.post(Endpoint.exportStreamManager_getExportStream, { handle: this.exportStream.handle }).then(response => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.exportStreamManager_getExportStream), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
            if (response && response.exportStream) {
                this.exportStream = new ExportStream(response.exportStream);
                this.exportOptions = this.exportStream.options;

                this.extraOptions.maxRecordsPerFile = false;
                if (!this.exportOptions.maxManifestRecords) {
                    this.exportOptions.maxManifestRecords = 0;
                } else if (!this.disabled.maxRecordsPerFile()) {
                    this.extraOptions.maxRecordsPerFile = true;
                }
                if (!this.disabled.includeFullText()) {
                    this.extraOptions.includeFullText = this.exportOptions.maximumEmbeddedTextSize === MAXIMUM_EMBEDDED_TEXT_SIZE;
                }
                if (this.exportOptions.dateTimeDelimiter === ' ') {
                    this.exportOptions.dateTimeDelimiter = 'Space';
                }

                // TODO-AFTER-BACKEND-FIX: remove this if-block after ST work is complete on ECASL-36124
                // currently backend is wrongly sending a default handle for overlayMetadataTemplateHandle when from UI a null value was sent
                if (!this.exportOptions.overlayManifest && this.exportOptions.overlayMetadataTemplateHandle) {
                    this.exportOptions.overlayMetadataTemplateHandle = null;
                }

                // TODO: remove this if-block after ST fix
                if (this.exportOptions.exportToDb) {
                    this.exportOptions.exportToDb = false;
                    this.exportOptions.tableName = '';
                }
            }
        }, error => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.exportStreamManager_getExportStream), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
        });
    }

    isLoadFileTypeSelected(): boolean {
        if (this.exportOptions.generateConcordance || this.exportOptions.generateCsv || this.exportOptions.generateEdrmXml ||
            this.exportOptions.exportToDb) {
            return true;
        }
        return false;
    }

    isSQLDBValid(): boolean {
        if (((this.exportOptions.exportToDb === true) && (this.exportComponent.tableNameDescriptionComponent.formValid === true)) ||
            (this.exportOptions.exportToDb === false)) {
            return true;
        }
        return false;
    }

    okEnabled(): boolean {
        return !this.isBusy;
    }

    ok(): void {
        if (this.validation.valid()) {
            const dataAreaName = this.extraOptions.exportDataAreas.find(eda => eda.handle === this.exportOptions.dataAreaHandle).name;
            let taskDescription = `Generate load file for ${this.exportStream.name}`;
            if (this.exportVolume) {
                taskDescription += ` ${this.exportVolume.name}`;
            }
            taskDescription += ` to ${dataAreaName}`;
            const updateDateSelections = this.extraOptions.updateDates.filter(ud => ud.selected).map(ud => ud.name);
            this.isBusyMessage = 'Generating load file...';
            this.isBusy = true;
            this.restService.post(Endpoint.viewManager_createExportViewManifest, {
                handle: this.exportVolume ? this.exportVolume.handle : this.exportStream.handle,
                consolidatedManifest: this.extraOptions.consolidatedManifest,
                generateConcordance: this.exportOptions.generateConcordance,
                generateCsv: this.exportOptions.generateCsv,
                generateEdrmXml: this.exportOptions.generateEdrmXml,
                generateDii: this.exportOptions.generateDii,
                generateLst: this.exportOptions.generateLst,
                excludeEmailHeadersInLoadFile: this.extraOptions.includeFullText ? this.exportOptions.excludeEmailHeadersInLoadFile : false,
                exportMetadataProfileTemplateHandle: this.exportOptions.metadataTemplateHandle,
                exportDataAreaHandle: this.exportOptions.dataAreaHandle,
                taskDescription: taskDescription,
                separateDupeManifest: this.exportOptions.separateDupeManifest,
                includeThreadAttach: this.exportOptions.includeThreadAttach,
                updateDateSelections: updateDateSelections,
                timezone: this.exportOptions.timezone,
                dateFormat: this.exportOptions.dateFormat,
                timeFormat: this.exportOptions.timeFormat,
                begAttachMode: this.exportOptions.begAttachMode,
                concordanceUnicodeFormat: this.exportOptions.concordanceUnicodeFormat,
                includeTextConversion: this.exportOptions.includeTextConversion,
                maximumEmbeddedTextSize: this.exportOptions.maximumEmbeddedTextSize,
                truncateEmbeddedText: false,
                maxManifestRecords: this.exportOptions.maxManifestRecords,
                overlayManifest: this.exportOptions.overlayManifest,
                // TODO-AFTER-BACKEND-FIX: rename param to overlayMetadataTemplateHandle
                exportOverlayMetadataProfileTemplateHandle: this.exportOptions.overlayMetadataTemplateHandle,
                includeAllMasterDuplicates: this.exportOptions.includeAllMasterDuplicates,
                includeVolumeLabelInPath: this.exportOptions.includeVolumeLabelInPath,
                dateTimeDelimiter: this.exportOptions.dateTimeDelimiter === 'Space' ? ' ' : this.exportOptions.dateTimeDelimiter,
                manifestSizeUnits: this.exportOptions.manifestSizeUnits,
                basePath: this.exportOptions.basePath
            }).then(response => {
                this.isBusy = false;
                if (response) {
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.TASK);
                }
            }, error => {
                this.isBusy = false;
            });
            this.closeModal();
        }
    }

    restrictNumeric = (event): boolean => {
        if (event) {
            return (event.charCode === 8 || event.charCode === 0) ? false : event.charCode >= 48 && event.charCode <= 57;
        }
        return false;
    }

    parseNumeric = (modelName: string, min?: number, max?: number): void => {
        if (modelName) {
            const field = _.get(this, modelName);
            if (field) {
                let val = parseInt(field, 10);
                if (isNaN(val)) {
                    return;
                }
                if (isNumber(min) && min > val) {
                    val = min;
                }
                if (isNumber(max) && val > max) {
                    val = max;
                }
                _.set(this, modelName, val);
            }
        }
    }

    getExportDatabaseConnection() {
        this.isBusy = true;
        this.isBusyMessage = 'Checking export database connection...';
        this.restService.post(Endpoint.projectManager_getExportOptions, { projectHandle: this.commonService.currentProject.handle }).then(response => {
            this.isBusy = false;
            if (response && response.selectedExportDatabaseConnection) {
                this.extraOptions.selectedExportDatabaseConnection = response.selectedExportDatabaseConnection;
                if (response.exportOptions && response.exportOptions.tableName) {
                    this.exportComponent.tableNameDescriptionComponent.name = response.exportOptions.tableName;
                }

                if (isNullOrUndefined(this.exportComponent.tableNameDescriptionComponent.name) || DrObject.isEmpty(this.exportComponent.tableNameDescriptionComponent.name)) {
                    this.formatTableName(this.commonService.currentProject.name, this.extraOptions.exportStreamName);
                }

                this.validation.tableName.invalid = !this.exportComponent.tableNameDescriptionComponent.formValid;
                this.exportComponent.tableNameNeedsValidation = false;
            } else {
                this.commonService.openErrorModal('No database connection has been selected in the "Export\nDatabase" dropdown in Project Settings > Export Settings.');
                this.exportOptions.exportToDb = false;
            }
        }, error => {
            this.isBusy = false;
            this.exportOptions.exportToDb = false;
        });
    }

    formatTableName(projectName: string, exportStreamName: string) {
        // remove leading, trailing, and embedded white spaces
        const formattedProjectName = projectName.replace(/ /g, '_').trim();
        const formattedExportStreamName = exportStreamName.replace(/ /g, '_').trim();
        const formattedTableName = `DR_${formattedProjectName}_${formattedExportStreamName}`;

        // validate name
        this.validation.tableName.errorMessage = null;
        this.exportOptions.tableName = formattedTableName;
        this.tableNameCheck();
        return;
    }

    setTableNameState(valid: boolean): void {
        // assume valid
        this.validation.tableName.hasValidCharacters = true;
        this.exportComponent.tableNameDescriptionComponent.formValid = true;
        this.validation.tableName.invalid = false;

        if (valid === false) {
            this.validation.tableName.hasValidCharacters = false;
            this.exportComponent.tableNameDescriptionComponent.formValid = false;
            this.validation.tableName.invalid = true;
        }
    }

    tableNameCheck() {
        this.exportComponent.tableNameDescriptionComponent.name = this.exportOptions.tableName;
        this.exportComponent.tableNameDescriptionComponent.validateNameError = null;
        const empty = DrObject.isEmpty(this.exportComponent.tableNameDescriptionComponent.name);
        if (empty) {
            this.validation.tableName.errorMessage = `Please specify the DB Table Name.`;
            this.setTableNameState(false);
        } else {
            this.tableNameCheckCharacters(this.exportComponent.tableNameDescriptionComponent.name, empty);
        }
    }

    tableNameCheckForErrors() {
        if (!DrObject.isEmpty(this.exportComponent.tableNameDescriptionComponent.name) && !this.exportComponent.tableNameDescriptionComponent.formValid) {
            this.validation.tableName.errorMessage = this.exportComponent.tableNameDescriptionComponent.validateNameError;
            this.validation.tableName.invalid = true;
        }
    }

    tableNameCheckCharacters(name: string, empty: boolean) {
        if (!empty) {
            this.validation.tableName.hasValidCharacters = false;
            this.exportComponent.tableNameDescriptionComponent.onNameChanged();
            this.setTableNameState(this.exportComponent.tableNameDescriptionComponent.formValid);
            if (!this.exportComponent.tableNameDescriptionComponent.formValid) {
                this.validation.tableName.errorMessage = this.exportComponent.tableNameDescriptionComponent.validateNameError;
                this.setTableNameState(false);
            }
        }
    }

    validateTableName(): void {
        this.exportComponent.tableNameDescriptionComponent.name = this.exportOptions.tableName.replace(/^\s+/, '');  // remove leading whitespaces;
        const empty = DrObject.isEmpty(this.exportComponent.tableNameDescriptionComponent.name);
        if (!empty && !this.validation.tableName.invalid && this.validation.tableName.hasValidCharacters) {
            this.isBusyMessage = `Validating DB Table Name ...`;
            this.exportComponent.tableNameDescriptionComponent.name = this.exportOptions.tableName;
            this.exportComponent.validateName(this.exportComponent.tableNameDescriptionComponent);
        }
    }

    setDBTableStates() {
        this.isSQLCheckboxDisabled();
        this.isTableNameDisabled();
    }

    isTableNameDisabled(): boolean {
        if (this.isExportVolume && (this.exportOptions.exportToDb === true)) {
            return false;
        }
        return true;
    }

    isSQLCheckboxDisabled(): boolean {
        return true;
    }
}
